import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthService } from '../services/api/auth/AuthService';
import { AcessoControleService } from '../services/api/controle/AcessoControleService';

interface IAuthContextData {
  logout: () => void;
  isAuthenticated: boolean;
  isSistema: boolean;
  login: (login: string, senha: string) => Promise<string | void>;
  sistema: (sistemas: string)  => void;
  logoutsistema: () => void;
}
const AuthContext = createContext({} as IAuthContextData);
const LOCAL_STORAGE_KEY__ACCESS_TOKEN = 'APP_ACCESS_TOKEN';
interface IAuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [accessSistema, setAccessSistema] = useState<string>();

  useEffect(() => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);

    if (!accessToken) {
      setAccessToken(undefined);
      return;
    }

    const token = JSON.parse(accessToken);
    // Este front é o painel de controle de empresas — a permissão é sempre conferida no
    // backend (usuario.controle no cadastro mestre, ver EnsureControleAdmin.ts), nunca por
    // algo decodificado do próprio token no navegador.
    AcessoControleService.verificar().then((temAcesso) => {
      if (temAcesso) {
        setAccessToken(token);
      } else {
        // Token de uma sessão anterior (ou de outro usuário) sem acesso ao painel —
        // não deixa a sessão "presa" autenticada sem conseguir usar nada.
        localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
        setAccessToken(undefined);
      }
    });
  }, []);
  const handleLogin = useCallback(async (login: string, senha: string) => {
    const result = await AuthService.auth(login, senha);

    if (result instanceof Error) {
      throw new Error(result.message);
    } else {
      // Precisa salvar antes de perguntar ao backend: o interceptor de request do Api lê o
      // token do localStorage a cada chamada, inclusive nesta.
      localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, JSON.stringify(result.accessToken));

      const temAcesso = await AcessoControleService.verificar();
      if (!temAcesso) {
        localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
        throw new Error('Este usuário não tem permissão para acessar o painel de controle.');
      }

      setAccessToken(result.accessToken);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    setAccessToken(undefined);
    setAccessSistema('');
  }, []);

  const handleLogoutsistema = useCallback(() => {
    setAccessSistema('');
  }, []);

  const handleSistema = useCallback((sistemas: string) => {
    setAccessSistema(sistemas);
  }, []);
  const isAuthenticated = useMemo(() => !!accessToken, [accessToken]);
  const isSistema = useMemo(() => !!accessSistema, [accessSistema]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isSistema, sistema: handleSistema,  logoutsistema: handleLogoutsistema,  login: handleLogin, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
};
export const useAuthContext = () => useContext(AuthContext);
